import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  bookingApi,
  type AvailabilityResponse,
  type AvailabilitySlot,
  type ClubInfo,
  type EquipmentItem,
  type GameKind
} from '@/api/client'
import { FloorMap, type FloorTableStatus } from '@billiard/ui'
import { dateKey, dayLabel, fullDateTime, money, nextDays, timeLabel } from '@/lib/format'
import { Cur } from '@/lib/currency'
import { hideBackButton, openLink, requestContact, showBackButton, tgUser } from '@/lib/telegram'

const TOTAL_STEPS = 4 // kind, time, equipment, confirm (done — отдельный экран)

function durationLabel(min: number): string {
  if (min % 60 === 0) return `${min / 60} ч`
  if (min > 60) return `${Math.floor(min / 60)} ч ${min % 60} мин`
  return `${min} мин`
}

function errMsg(e: unknown): string {
  return e instanceof ApiError ? e.message : 'Что-то пошло не так. Попробуйте ещё раз.'
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function BookingPage({
  slug,
  notTelegram,
  onGoMyBookings
}: {
  slug: string
  notTelegram?: boolean
  onGoMyBookings?: () => void
}): React.JSX.Element {
  const [club, setClub] = useState<ClubInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [fatal, setFatal] = useState<string | null>(null)

  const [step, setStep] = useState(0)
  const [gameKind, setGameKind] = useState<GameKind | null>(null)
  const [date, setDate] = useState<string | null>(null)
  const [avail, setAvail] = useState<AvailabilityResponse | null>(null)
  const [availLoading, setAvailLoading] = useState(false)
  const [slot, setSlot] = useState<AvailabilitySlot | null>(null)
  // Выбор стола: id конкретного стола | 'any' (любой, автоназначение) | null (ещё не выбран).
  const [tableSel, setTableSel] = useState<string | 'any' | null>(null)
  const [durationMin, setDurationMin] = useState(0)

  const [equipment, setEquipment] = useState<EquipmentItem[]>([])
  const [qty, setQty] = useState<Record<string, number>>({})

  const [name, setName] = useState('')
  // Телефон, известный серверу (привязанный клиент / шэринг контакта боту). null → надо поделиться.
  const [knownPhone, setKnownPhone] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [result, setResult] = useState<Awaited<
    ReturnType<typeof bookingApi.tmaCreateBooking>
  > | null>(null)

  const tz = club?.timezone ?? null

  // Имя типа игры по коду (из справочника клуба); фолбэк — сам код.
  const kindName = useCallback(
    (code: string | null | undefined): string =>
      (code ? club?.gameTypes.find((g) => g.code === code)?.name : null) ?? (code || '—'),
    [club]
  )

  // ── Загрузка клуба + оборудования + профиля пользователя ──
  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.all([
      bookingApi.clubInfo(slug),
      bookingApi.equipment(slug).catch(() => []),
      bookingApi.tmaMe(slug).catch(() => null)
    ])
      .then(([info, eq, me]) => {
        if (!alive) return
        setClub(info)
        setEquipment(eq)
        setDurationMin(Math.max(info.slotMinutes, info.minMinutes))
        if (info.gameKinds.length === 1) setGameKind(info.gameKinds[0])
        setKnownPhone(me?.phone ?? null)
        setName((me?.name || me?.tgFirstName || tgUser()?.first_name || '').trim())
      })
      .catch((e) => alive && setFatal(errMsg(e)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [slug])

  // Другой тип игры или другая дата — набор столов другой, выбранный сбрасываем.
  // От длительности набор столов не зависит, поэтому её смена выбор стола не рушит.
  useEffect(() => {
    setTableSel(null)
  }, [slug, gameKind, date])

  // ── Доступность при выборе типа+даты+длительности ──
  // Длительность в запросе обязательна по смыслу: сервер проверяет столы на весь
  // бронируемый интервал, а не на один шаг сетки. Без неё выдача предлагала слот,
  // который упирался в чужую бронь через час, и коммит отвечал 409 — уже после SMS.
  useEffect(() => {
    if (!gameKind || !date || !durationMin) return
    let alive = true
    setAvailLoading(true)
    setSlot(null) // на новой длительности прежний слот мог перестать быть свободным
    bookingApi
      .availability(slug, date, gameKind, durationMin)
      .then((a) => alive && setAvail(a))
      .catch((e) => alive && setErr(errMsg(e)))
      .finally(() => alive && setAvailLoading(false))
    return () => {
      alive = false
    }
  }, [slug, gameKind, date, durationMin])

  // Если для выбранного типа игры доступен только один стол (например, единственный
  // плейстейшен) — выбираем его сразу, не заставляя клиента тыкать в очевидное.
  useEffect(() => {
    if (!club?.allowTableChoice || !avail || avail.tables.length !== 1) return
    const only = avail.tables[0].id
    setTableSel((cur) => cur ?? only)
  }, [club, avail])

  const go = useCallback((n: number) => {
    setErr(null)
    setStep(n)
  }, [])

  // ── Нативная кнопка «Назад» Telegram (шаги 1..3 → предыдущий шаг) ──
  useEffect(() => {
    if (step >= 1 && step < TOTAL_STEPS) {
      const back = () => go(step - 1)
      showBackButton(back)
      return () => hideBackButton(back)
    }
    hideBackButton()
    return undefined
  }, [step, go])

  const durations = useMemo(() => {
    if (!club) return []
    const out: number[] = []
    for (let m = club.minMinutes; m <= club.maxMinutes; m += club.slotMinutes) out.push(m)
    return out
  }, [club])

  const days = useMemo(() => nextDays(14), [])
  const todayKey = useMemo(() => dateKey(new Date(), tz), [tz])
  const tomorrowKey = useMemo(() => dateKey(new Date(Date.now() + 86400000), tz), [tz])

  const equipmentTotal = useMemo(
    () => equipment.reduce((sum, it) => sum + (qty[it.id] ?? 0) * it.price, 0),
    [equipment, qty]
  )

  // ── Миникарта зала: только при разрешённом выборе стола и настроенном плане ──
  const floorMap = club?.allowTableChoice ? club.floorMap : null
  const hasMap = !!floorMap && floorMap.tables.length > 0

  // Столы, у которых на выбранный день не осталось ни одного свободного слота.
  const busyAllDay = useMemo(() => {
    if (!avail) return new Set<string>()
    const free = new Set<string>()
    for (const s of avail.slots) for (const id of s.freeTableIds) free.add(id)
    return new Set(avail.tables.filter((t) => !free.has(t.id)).map((t) => t.id))
  }, [avail])

  // Статусы для карты: стола нет в avail.tables (другой тип игры / снят с онлайн-брони)
  // → inactive; занят весь день → busy; иначе free.
  const mapStatuses = useMemo(() => {
    const st: Record<string, FloorTableStatus> = {}
    if (!floorMap) return st
    const inAvail = new Set((avail?.tables ?? []).map((t) => t.id))
    for (const t of floorMap.tables) {
      st[t.id] = !inAvail.has(t.id) ? 'inactive' : busyAllDay.has(t.id) ? 'busy' : 'free'
    }
    return st
  }, [floorMap, avail, busyAllDay])

  // Столы дня, не размещённые на плане (добавлены после настройки плана) — пилюли под картой.
  const offPlanTables = useMemo(() => {
    if (!hasMap || !avail || !floorMap) return []
    const onMap = new Set(floorMap.tables.map((t) => t.id))
    return avail.tables.filter((t) => !onMap.has(t.id))
  }, [hasMap, floorMap, avail])

  // Выбор стола; уже выбранный слот сбрасываем, если он занят для нового стола.
  const pickTable = useCallback(
    (id: string | 'any'): void => {
      setTableSel(id)
      if (id !== 'any' && slot && !slot.freeTableIds.includes(id)) setSlot(null)
    },
    [slot]
  )

  const endsAt = slot ? slot.startsAt + durationMin * 60000 : 0

  // ── Шэринг контакта: телефон уходит боту → перечитываем профиль, пока не появится ──
  const refreshPhone = useCallback(async (): Promise<boolean> => {
    try {
      const me = await bookingApi.tmaMe(slug)
      if (me.phone) {
        setKnownPhone(me.phone)
        return true
      }
    } catch {
      // игнор — покажем общий статус ниже
    }
    return false
  }, [slug])

  const sharePhone = useCallback(async () => {
    setSharing(true)
    setErr(null)
    const ok = await requestContact()
    if (!ok) {
      setSharing(false)
      setErr('Не удалось получить номер. Нажмите «Поделиться номером» ещё раз или откройте бота.')
      return
    }
    // Телефон дойдёт до бота асинхронно — опрашиваем профиль несколько раз.
    for (let i = 0; i < 6; i++) {
      await sleep(800)
      if (await refreshPhone()) {
        setSharing(false)
        return
      }
    }
    setSharing(false)
    setErr('Номер сохраняется… Подождите пару секунд и нажмите «Обновить».')
  }, [refreshPhone])

  const confirm = useCallback(async () => {
    if (!slot || !knownPhone) return
    setBusy(true)
    setErr(null)
    try {
      const equipmentArr = Object.entries(qty)
        .filter(([, q]) => q > 0)
        .map(([itemId, q]) => ({ itemId, qty: q }))
      const res = await bookingApi.tmaCreateBooking(slug, {
        name: name.trim(),
        gameKind: gameKind ?? undefined,
        // 'any' = автоназначение на сервере (как при выключенном выборе стола).
        tableId: club?.allowTableChoice && tableSel && tableSel !== 'any' ? tableSel : undefined,
        startsAt: slot.startsAt,
        endsAt: slot.startsAt + durationMin * 60000,
        equipment: equipmentArr.length ? equipmentArr : undefined
      })
      setResult(res)
      setStep(TOTAL_STEPS)
    } catch (e) {
      setErr(errMsg(e))
    } finally {
      setBusy(false)
    }
  }, [slug, knownPhone, qty, name, gameKind, slot, durationMin, club, tableSel])

  // ── Состояния загрузки/ошибки ──
  if (loading) {
    return (
      <div className="page">
        <div className="center">
          <div className="spinner" />
        </div>
      </div>
    )
  }
  if (fatal || !club) {
    return (
      <div className="page">
        <div className="shell">
          <div className="card">
            <div className="card-body">
              <p className="empty">{fatal ?? 'Клуб не найден или бронирование недоступно.'}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (club.gameKinds.length === 0) {
    return (
      <div className="page">
        <div className="shell">
          <div className="brand">
            <div className="logo">
              8<b>Ball</b>
            </div>
            <div className="sub">{club.name}</div>
          </div>
          <div className="card">
            <div className="card-body">
              <p className="empty">
                Онлайн-бронирование для этого клуба пока недоступно — столы ещё не настроены.
                Пожалуйста, свяжитесь с клубом напрямую, чтобы забронировать стол.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const setQ = (id: string, delta: number) =>
    setQty((m) => {
      const next = Math.max(0, (m[id] ?? 0) + delta)
      return { ...m, [id]: next }
    })

  return (
    <div className="page">
      <div className="shell">
        <div className="brand">
          <div className="logo">
            8<b>Ball</b>
          </div>
          <div className="sub">{club.name}</div>
        </div>

        <div className="card">
          {step < TOTAL_STEPS && (
            <div className="stepper">
              {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                <div key={i} className={`dot ${i <= step ? 'on' : ''}`} />
              ))}
            </div>
          )}

          <div className="card-body">
            {err && <div className="error">{err}</div>}

            {/* ─── Шаг 0: тип игры ─── */}
            {step === 0 && (
              <>
                <div className="step-title">Выберите игру</div>
                <div className="step-hint">Тип стола для вашей партии</div>
                <div className="choices">
                  {club.gameTypes.map((g) => (
                    <button
                      key={g.code}
                      className={`choice ${gameKind === g.code ? 'on' : ''}`}
                      onClick={() => setGameKind(g.code)}
                    >
                      <div
                        className={`ball ${g.code}`}
                        style={g.color ? { background: g.color, color: '#fff' } : undefined}
                      >
                        {g.code === 'pool' ? '8' : '●'}
                      </div>
                      <div className="name">{g.name}</div>
                    </button>
                  ))}
                </div>
                <div className="actions">
                  <button className="btn btn-primary" disabled={!gameKind} onClick={() => go(1)}>
                    Далее
                  </button>
                </div>
              </>
            )}

            {/* ─── Шаг 1: день + время + длительность ─── */}
            {step === 1 && (
              <>
                <div className="step-title">Дата и время</div>
                <div className="step-hint">Когда вам удобно прийти?</div>

                <div className="chips">
                  {days.map((d) => {
                    const key = dateKey(d, tz)
                    return (
                      <button
                        key={key}
                        className={`chip ${date === key ? 'on' : ''}`}
                        onClick={() => setDate(key)}
                      >
                        <div className="d">{dayLabel(d, tz, todayKey, tomorrowKey)}</div>
                      </button>
                    )
                  })}
                </div>

                {date && (
                  <>
                    <div className="field-label">Длительность</div>
                    <div className="pills">
                      {durations.map((m) => (
                        <button
                          key={m}
                          className={`pill ${durationMin === m ? 'on' : ''}`}
                          onClick={() => setDurationMin(m)}
                        >
                          {durationLabel(m)}
                        </button>
                      ))}
                    </div>

                    {availLoading ? (
                      <div className="center" style={{ minHeight: 80 }}>
                        <div className="spinner" />
                      </div>
                    ) : avail && avail.slots.length > 0 ? (
                      hasMap && floorMap ? (
                        <>
                          {/* ── Миникарта: сначала стол, затем слоты по нему ── */}
                          <div className="field-label">Выберите стол на плане</div>
                          <div className="floor-map-wrap">
                            <FloorMap
                              layout={floorMap}
                              gameTypes={club.gameTypes}
                              statuses={mapStatuses}
                              selectedId={tableSel !== 'any' ? tableSel : null}
                              onSelect={pickTable}
                              fitToContent
                            />
                          </div>
                          <div className="fm-legend">
                            <span className="fm-legend-item is-free">свободен</span>
                            <span className="fm-legend-item is-busy">занят весь день</span>
                          </div>
                          <div className="pills">
                            <button
                              className={`pill ${tableSel === 'any' ? 'on' : ''}`}
                              onClick={() => pickTable('any')}
                            >
                              Любой стол
                            </button>
                            {offPlanTables.map((t) => (
                              <button
                                key={t.id}
                                disabled={busyAllDay.has(t.id)}
                                className={`pill ${tableSel === t.id ? 'on' : ''}`}
                                onClick={() => pickTable(t.id)}
                              >
                                {t.name}
                              </button>
                            ))}
                          </div>

                          <div className="field-label">Время начала</div>
                          <div className="slots">
                            {avail.slots.map((s) => {
                              const free =
                                tableSel && tableSel !== 'any'
                                  ? s.freeTableIds.includes(tableSel)
                                  : s.freeTableIds.length > 0
                              return (
                                <button
                                  key={s.startsAt}
                                  disabled={!free}
                                  className={`slot ${slot?.startsAt === s.startsAt ? 'on' : ''}`}
                                  onClick={() => setSlot(s)}
                                >
                                  {timeLabel(s.startsAt, tz)}
                                </button>
                              )
                            })}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="field-label">Время начала</div>
                          <div className="slots">
                            {avail.slots.map((s) => {
                              const free = s.freeTableIds.length > 0
                              return (
                                <button
                                  key={s.startsAt}
                                  disabled={!free}
                                  className={`slot ${slot?.startsAt === s.startsAt ? 'on' : ''}`}
                                  onClick={() => {
                                    setSlot(s)
                                    setTableSel(null)
                                  }}
                                >
                                  {timeLabel(s.startsAt, tz)}
                                </button>
                              )
                            })}
                          </div>

                          {/* Выбор конкретного стола — только если владелец включил в БО */}
                          {slot && club.allowTableChoice && (
                            <>
                              <div className="field-label">Стол</div>
                              <div className="pills">
                                {avail.tables
                                  .filter((t) => slot.freeTableIds.includes(t.id))
                                  .map((t) => (
                                    <button
                                      key={t.id}
                                      className={`pill ${tableSel === t.id ? 'on' : ''}`}
                                      onClick={() => setTableSel(t.id)}
                                    >
                                      {t.name}
                                    </button>
                                  ))}
                              </div>
                            </>
                          )}
                        </>
                      )
                    ) : (
                      <div className="empty">На этот день нет свободных слотов</div>
                    )}
                  </>
                )}

                <div className="actions">
                  <button className="btn btn-ghost back" onClick={() => go(0)}>
                    Назад
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!slot || (club.allowTableChoice && !tableSel)}
                    onClick={() => go(2)}
                  >
                    Далее
                  </button>
                </div>
              </>
            )}

            {/* ─── Шаг 2: оборудование ─── */}
            {step === 2 && (
              <>
                <div className="step-title">Оборудование</div>
                <div className="step-hint">Можно добавить кий, перчатку и др. (необязательно)</div>
                {equipment.length === 0 ? (
                  <div className="empty">Дополнительное оборудование недоступно</div>
                ) : (
                  <div>
                    {equipment.map((it) => (
                      <div className="eq-row" key={it.id}>
                        <div className="eq-info">
                          <div className="eq-name">{it.name}</div>
                          <div className="eq-price">
                            {it.price > 0 ? (
                              <>
                                {money(it.price)} <Cur display={club?.currencyDisplay} />
                              </>
                            ) : (
                              'бесплатно'
                            )}
                            {it.deposit > 0 ? (
                              <>
                                {' · залог '}
                                {money(it.deposit)} <Cur display={club?.currencyDisplay} />
                              </>
                            ) : (
                              ''
                            )}
                          </div>
                        </div>
                        <div className="stepperq">
                          <button onClick={() => setQ(it.id, -1)} aria-label="минус">
                            −
                          </button>
                          <span className="qv">{qty[it.id] ?? 0}</span>
                          <button onClick={() => setQ(it.id, +1)} aria-label="плюс">
                            +
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="actions">
                  <button className="btn btn-ghost back" onClick={() => go(1)}>
                    Назад
                  </button>
                  <button className="btn btn-primary" onClick={() => go(3)}>
                    Далее
                  </button>
                </div>
              </>
            )}

            {/* ─── Шаг 3: подтверждение (имя + телефон из Telegram) ─── */}
            {step === 3 && (
              <>
                <div className="step-title">Подтверждение</div>
                <div className="step-hint">Имя и телефон для связи по брони</div>

                <div className="input-group">
                  <label>Имя</label>
                  <input
                    className="input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Например, Сергей"
                    autoComplete="name"
                  />
                </div>

                <div className="input-group">
                  <label>Телефон</label>
                  {knownPhone ? (
                    <div className="phone-known">
                      <span className="phone-val">{knownPhone}</span>
                      <span className="phone-note">из Telegram</span>
                    </div>
                  ) : (
                    <>
                      <button
                        className="btn btn-share"
                        disabled={sharing || notTelegram}
                        onClick={sharePhone}
                      >
                        {sharing ? 'Получаем номер…' : '📱 Поделиться номером'}
                      </button>
                      <button className="linkbtn" onClick={refreshPhone} disabled={sharing}>
                        Обновить
                      </button>
                      {notTelegram && (
                        <div className="tg-note">
                          Шэринг номера доступен только внутри Telegram.
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="actions">
                  <button className="btn btn-ghost back" onClick={() => go(2)}>
                    Назад
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={busy || !knownPhone || name.trim().length < 1}
                    onClick={confirm}
                  >
                    {busy ? 'Бронируем…' : 'Подтвердить бронь'}
                  </button>
                </div>
                <div className="consent-note">
                  Подтверждая бронь, вы соглашаетесь с{' '}
                  <a
                    href="https://8ballpos.by/policy"
                    onClick={(e) => {
                      e.preventDefault()
                      openLink('https://8ballpos.by/policy')
                    }}
                  >
                    политикой обработки персональных данных
                  </a>
                  .
                </div>
              </>
            )}

            {/* ─── Готово ─── */}
            {step === TOTAL_STEPS && result && (
              <div className="success">
                <div className="seal">✓</div>
                <h2>Бронь подтверждена!</h2>
                <p>Ждём вас в клубе. Бронь можно посмотреть во вкладке «Мои брони».</p>
                <div className="summary">
                  <div className="row">
                    <span className="k">Игра</span>
                    <span className="v">{kindName(result.gameKind)}</span>
                  </div>
                  <div className="row">
                    <span className="k">Когда</span>
                    <span className="v">{fullDateTime(result.startsAt, tz)}</span>
                  </div>
                  <div className="row">
                    <span className="k">До</span>
                    <span className="v">{timeLabel(result.endsAt, tz)}</span>
                  </div>
                  <div className="row">
                    <span className="k">На имя</span>
                    <span className="v">{result.customerName}</span>
                  </div>
                  {result.items.length > 0 && (
                    <div className="row">
                      <span className="k">Оборудование</span>
                      <span className="v">
                        {result.items.map((i) => `${i.name ?? '—'}×${i.qty}`).join(', ')}
                      </span>
                    </div>
                  )}
                </div>
                {onGoMyBookings && (
                  <div className="actions">
                    <button className="btn btn-primary" onClick={onGoMyBookings}>
                      Мои брони
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {step < TOTAL_STEPS && slot && step >= 1 && (
          <div className="footer-note">
            {gameKind ? kindName(gameKind) : ''} · {timeLabel(slot.startsAt, tz)}–
            {timeLabel(endsAt, tz)}
            {tableSel && tableSel !== 'any'
              ? ` · ${avail?.tables.find((t) => t.id === tableSel)?.name ?? ''}`
              : ''}
            {equipmentTotal > 0 ? (
              <>
                {' · оборуд. '}
                {money(equipmentTotal)} <Cur display={club?.currencyDisplay} />
              </>
            ) : (
              ''
            )}
          </div>
        )}
      </div>
    </div>
  )
}
